import React, { useEffect, useState } from 'react';
import { Alert, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { TiWarningOutline } from 'react-icons/ti';
import { useNavigate, useParams } from 'react-router-dom';
import { IParams } from '../../../../../model/shared/IParams';
import EmptyState from '../../../../../shared/components/placeholder/EmptyState';
import { useSearchQuery } from '../../../../../shared/hooks/useSearchQuery';
import { ErrorToast, SuccessToast } from '../../../../../utils/toasters';
import { ContractService } from '../service';
import { CheckoutCard } from './CheckoutCard';
import { IContractsCheckout } from './dto/IContractsCheckout';
import { BackButton } from '../../../../../shared/components/button/BackButton';
import { IContractOverviewCart } from '../contract/dto/IContractOverviewCart';
import BookingOverviewContractsCard from '../contract/BookingOverviewContractsCard';
import { InternalRoutes } from '../../../../../enum/InternalRoutes';

export function CheckoutContracts() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams<keyof IParams>();
    const hasSent = useSearchQuery('hasSent') === 'true';

    const [checkoutContracts, setCheckoutContracts] = useState<IContractsCheckout>();
    const [overviewCart, setOverviewCart] = useState<IContractOverviewCart>();
    const [updateOverviewCart, setUpdateOverviewCart] = useState(false);
    const [updateContracts, setUpdateContracts] = useState(false);

    useEffect(() => {
        ContractService.getContractsCheckout(id)
            .then(response => {
                setCheckoutContracts(response);
            })
            .catch(err => ErrorToast(err));
    }, [updateContracts]);

    useEffect(() => {
        ContractService.getContractCartInfo(+id!).then(response => {
            setOverviewCart(response);
        });
    }, [updateOverviewCart]);

    function deselectOffer(offerId: number) {
        ContractService.selectContract(offerId)
            .then(response => {
                if (response) {
                    setUpdateContracts(!updateContracts);
                    SuccessToast(t('general.toasts.successfullyRemovedOffer'));
                    setUpdateOverviewCart(!updateOverviewCart);
                }
            })
            .catch(error => ErrorToast(error));
    }

    function acceptOffer(offerId: number) {
        ContractService.acceptContract(offerId)
            .then(response => {
                SuccessToast(t('general.toasts.successfullyAccepeted'));
                setUpdateContracts(!updateContracts);
                setOverviewCart(response);
            })
            .catch(error => ErrorToast(error));
    }

    // function declineOffer(offerId: number) {
    //     BookingService.declineOffer(offerId)
    //         .then((response) => {
    //             SuccessToast(t('general.toasts.successDeclined'));
    //             setUpdateOffers(!updateOffers);
    //         }).catch(error => ErrorToast(error));
    // }

    const bookSelectedContract = () => {
        ContractService.bookSelectedOffer(id)
            .then(response => {
                SuccessToast(t('general.toasts.bookedSelectedOffer'));
                setCheckoutContracts(response);
                setUpdateOverviewCart(!updateOverviewCart);
            })
            .catch(err => ErrorToast(err));
    };

    return (
        <Row className="mb-5 mb-md-0 pb-5 pb-md-0">
            <Col xl={9}>
                <Row>
                    <BackButton />
                    {(checkoutContracts?.selectedOffersCount ?? 0) > 0 && (
                        <div className="col-12 pl-0 mt-3">
                            <h4 className="w-100 d-flex justify-content-between">
                                {t('brand.campaign.book.contract.selected_contract')}(
                                {checkoutContracts?.selectedOffersCount})
                            </h4>
                        </div>
                    )}

                    <CheckoutCard
                        deselectContract={deselectOffer}
                        contracts={checkoutContracts?.selectedOffers}
                        bookingStatus="selected"
                        acceptContract={acceptOffer}
                        declineContract={deselectOffer}
                    />

                    {hasSent && (
                        <div className="col-12 pl-0 mt-3">
                            <Alert variant="warning" className="box-shadow w-fit-content d-flex align-items-center">
                                <TiWarningOutline size={20} />
                                {t('brand.campaign.book.checkout.hasSentOffers')}
                                <a
                                    onClick={() =>
                                        navigate(`${InternalRoutes.BRAND_CAMPAIGN_CONTRACTS}/${id}`, {
                                            state: { isSentActive: true },
                                        })
                                    }
                                    className="ml-1"
                                >
                                    {t('brand.campaign.book.checkout.takeALook')}
                                </a>
                            </Alert>
                        </div>
                    )}

                    {(checkoutContracts?.bookedOffersCount ?? 0) > 0 && (
                        <div className="col-12 pl-0 mt-3">
                            <h4 className="w-100">
                                {t('brand.campaign.book.offer.booked_offers')}({checkoutContracts?.bookedOffersCount})
                            </h4>
                        </div>
                    )}
                    <CheckoutCard
                        deselectContract={deselectOffer}
                        contracts={checkoutContracts?.bookedOffers}
                        bookingStatus="booked"
                    />

                    {!checkoutContracts?.bookedOffers?.length && !checkoutContracts?.selectedOffers?.length && (
                        <div className="card mt-3">
                            <div className="card-body">
                                <EmptyState />
                            </div>
                        </div>
                    )}
                </Row>
            </Col>
            <Col xl={3}>
                <BookingOverviewContractsCard
                    contracts={checkoutContracts?.selectedOffers}
                    bookSelectedContracts={bookSelectedContract}
                    overviewCartInfo={overviewCart}
                />
            </Col>
        </Row>
    );
}
